import { create } from 'zustand';
import { storage, STORAGE_KEYS } from '@utils/storage';
import { authService } from '@services/authService';
import { useCartStore } from './cartStore';
import { useRestaurantStore } from './restaurantStore';
import type { User, AuthTokens } from '@/types';

type SocialProvider = 'google' | 'facebook' | 'apple';

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, surname: string) => Promise<void>;
  socialLogin: (provider: SocialProvider, token: string) => Promise<void>;
  logout: () => Promise<void>;
  clearLocalSession: () => void;
  loadFromStorage: () => void;
  refreshUser: () => Promise<void>;
  updateProfile: (data: { phone?: string | null; email?: string }) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const { user, tokens } = await authService.login(email, password);
      
      storage.set(STORAGE_KEYS.AUTH_TOKENS, tokens);
      storage.set(STORAGE_KEYS.USER_DATA, user);
      storage.remove(STORAGE_KEYS.GUEST_ID);
      
      set({
        user,
        tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      if (user.mustChangePassword) {
        window.dispatchEvent(new CustomEvent('auth:mustChangePassword', { detail: { user } }));
      } else {
        try {
          await useCartStore.getState().syncFromBackend();
        } catch (cartError) {
          console.error('Failed to sync cart after login:', cartError);
        }
      }
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.message || 'Giriş başarısız oldu';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  register: async (email: string, password: string, name: string, surname: string) => {
    set({ isLoading: true, error: null });
    try {
      await authService.register(email, password, name, surname);
      set({ isLoading: false });
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.message || 'Kayıt başarısız oldu';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  socialLogin: async (provider: SocialProvider, token: string) => {
    set({ isLoading: true, error: null });
    try {
      const { user, tokens } = await authService.socialLogin(provider, token);
      
      storage.set(STORAGE_KEYS.AUTH_TOKENS, tokens);
      storage.set(STORAGE_KEYS.USER_DATA, user);
      storage.remove(STORAGE_KEYS.GUEST_ID);
      
      set({
        user,
        tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      try {
        await useCartStore.getState().syncFromBackend();
      } catch (cartError) {
        console.error('Failed to sync cart after social login:', cartError);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Giriş başarısız oldu';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
      if (tokens?.access_token && tokens?.refresh_token) {
        await authService.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      storage.remove(STORAGE_KEYS.AUTH_TOKENS);
      storage.remove(STORAGE_KEYS.USER_DATA);
      storage.remove(STORAGE_KEYS.GUEST_CART);
      useRestaurantStore.getState().reset();
      void useCartStore.getState().syncFromBackend();

      set({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  clearLocalSession: () => {
    storage.remove(STORAGE_KEYS.AUTH_TOKENS);
    storage.remove(STORAGE_KEYS.USER_DATA);
    storage.remove(STORAGE_KEYS.GUEST_CART);
    useRestaurantStore.getState().reset();
    void useCartStore.getState().syncFromBackend();
    set({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  },

  loadFromStorage: () => {
    const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
    const user = storage.get<User>(STORAGE_KEYS.USER_DATA);
    
    if (tokens && user) {
      set({
        tokens,
        user,
        isAuthenticated: true,
      });
    }
  },

  refreshUser: async () => {
    const { user, tokens } = useAuthStore.getState();
    if (!tokens?.access_token || !user) return;
    try {
      const freshUser = await authService.getCurrentUser();
      storage.set(STORAGE_KEYS.USER_DATA, freshUser);
      set({ user: freshUser });
    } catch {
      // Ignore - user may have logged out
    }
  },

  updateProfile: async (data: { phone?: string | null; email?: string }) => {
    const user = await authService.updateProfile(data);
    storage.set(STORAGE_KEYS.USER_DATA, user);
    set({ user });
  },

  clearError: () => set({ error: null }),
}));

// Load auth state from localStorage on app start
useAuthStore.getState().loadFromStorage();

// Listen for logout events from API interceptor
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    useAuthStore.getState().logout().catch(() => {
      // Ignore logout errors
    });
  });

  window.addEventListener('auth:clear', () => {
    useAuthStore.getState().clearLocalSession();
  });

  window.addEventListener('auth:tokens', (event: Event) => {
    const detail = (event as CustomEvent).detail as { tokens?: AuthTokens } | undefined;
    const tokens = detail?.tokens;
    if (!tokens) return;

    useAuthStore.setState((prev) => ({
      ...prev,
      user: prev.user ?? storage.get<User>(STORAGE_KEYS.USER_DATA),
      tokens,
      isAuthenticated: Boolean((prev.user ?? storage.get<User>(STORAGE_KEYS.USER_DATA)) && tokens?.access_token),
    }));
  });
}
